/**
 * @fileoverview Painel do gatilho "Status da OS": a integração Care, os status
 * que disparam, a conexão da API oficial e o que acontece com a conversa depois
 * do envio. O template e as variáveis ficam no "Enviar Mensagem" ligado a ele.
 *
 * Os status vêm da lista dos já vistos (cada aviso do Care acrescenta o dele);
 * um status que ainda não chegou pode ser digitado pelo nome. Guarda no nó:
 * integrationId, statuses [{ code, name }], whatsappId e
 * conversation { mode: closed | pending | open, queueId, userId }.
 */

import React, { useEffect, useState } from "react";
import { Tooltip, CircularProgress } from "@material-ui/core";
import { Check, Info, Plus, X } from "lucide-react";

import stripAccents from "../../../../utils/stripAccents";

const MODES = [
  { value: "closed", label: "Encerrada", desc: "A conversa é criada e já fica encerrada." },
  { value: "pending", label: "Aguardando", desc: "A conversa fica aguardando na fila escolhida." },
  { value: "open", label: "Atendendo", desc: "A conversa fica em atendimento com o usuário escolhido." },
];

// Mesma comparação do servidor: sem acento, minúsculas e espaços repetidos.
const comparable = (name) =>
  stripAccents(name)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const sameStatus = (a, b) => (a.code && b.code ? a.code === b.code : comparable(a.name) === comparable(b.name));

const Label = ({ classes, text, help }) => (
  <div className={classes.configLabelRow}>
    <span className={classes.configLabel}>{text}</span>
    {help && (
      <Tooltip title={help} placement="top" classes={{ tooltip: classes.configInfoTooltip }}>
        <span className={classes.configInfoIcon}>
          <Info size={13} />
        </span>
      </Tooltip>
    )}
  </div>
);

/** Lista de escolha única no estilo do painel (clicar no escolhido desmarca). */
const SingleList = ({ classes, items, selectedId, onSelect, empty, renderLabel }) => (
  <div className={classes.configDropdown} style={{ marginTop: 6 }}>
    {items.length === 0 && <div className={classes.configEmpty}>{empty}</div>}
    {items.map((item) => {
      const isSelected = Number(selectedId) === item.id;
      return (
        <div
          key={item.id}
          className={classes.configDropdownItem}
          role="option"
          aria-selected={isSelected}
          onClick={() => onSelect(isSelected ? null : item.id)}
        >
          <div className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}>
            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
          </div>
          {renderLabel ? renderLabel(item) : item.name}
        </div>
      );
    })}
  </div>
);

const OrderStatusPanel = ({ classes, nodeData, updateNodeData, whatsapps, queues, users, orderSources, reloadOrderSources, W }) => {
  const [typed, setTyped] = useState("");

  // Status novos chegam a cada aviso do Care: a lista é lida de novo ao abrir.
  useEffect(() => {
    if (reloadOrderSources) reloadOrderSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statuses = Array.isArray(nodeData.statuses) ? nodeData.statuses : [];
  const conversation = nodeData.conversation || {};
  const mode = MODES.some((m) => m.value === conversation.mode) ? conversation.mode : "closed";
  const source = (orderSources || []).find((s) => s.id === Number(nodeData.integrationId)) || null;
  const known = source ? source.statuses : [];
  const officialConnections = (whatsapps || []).filter((wa) => wa.provider === "meta-api");
  const people = (users || []).filter((u) => !u.isAI);

  const setStatuses = (next) => updateNodeData("statuses", next);
  const toggleStatus = (status) => {
    const has = statuses.some((s) => sameStatus(s, status));
    setStatuses(has ? statuses.filter((s) => !sameStatus(s, status)) : statuses.concat({ code: status.code || null, name: status.name }));
  };
  const addTyped = () => {
    const name = typed.replace(/\s+/g, " ").trim();
    if (!name) return;
    // Digitado igual a um já visto: usa o código dele.
    const match = known.find((s) => comparable(s.name) === comparable(name));
    const status = match ? { code: match.code || null, name: match.name } : { code: null, name };
    if (!statuses.some((s) => sameStatus(s, status))) setStatuses(statuses.concat(status));
    setTyped("");
  };
  const setConversation = (patch) => updateNodeData("conversation", { ...conversation, mode, ...patch });

  return (
    <React.Fragment>
      <div className={classes.configField}>
        <Label
          classes={classes}
          text="Integração"
          help="O Care que avisa as mudanças das OS. Cadastre em Integrações: lá fica o endereço para colar no Care."
        />
        {orderSources === null ? (
          <div className={classes.configEmpty}>
            <CircularProgress size={12} style={{ marginRight: 8 }} />
            Carregando integrações...
          </div>
        ) : (
          <SingleList
            classes={classes}
            items={orderSources}
            selectedId={nodeData.integrationId}
            empty='Nenhuma integração Care. Cadastre uma em "Integrações".'
            onSelect={(id) => {
              updateNodeData("integrationId", id);
              // Os status de outra integração não valem nesta.
              if (Number(id) !== Number(nodeData.integrationId)) updateNodeData("statuses", []);
            }}
            renderLabel={(s) => (
              <span>
                {s.name}
                {!s.isActive && <span style={{ color: W(0.45) }}> (desligada)</span>}
              </span>
            )}
          />
        )}
      </div>

      {nodeData.integrationId && (
        <div className={classes.configField}>
          <Label
            classes={classes}
            text="Quando a OS mudar para"
            help="A mensagem sai quando a OS muda para um destes status. Cada OS recebe a mensagem de cada status uma vez só, mesmo que volte a ele."
          />
          {statuses.length > 0 && (
            <div className={classes.configChips}>
              {statuses.map((s) => (
                <span key={(s.code || "") + ":" + s.name} className={classes.configChip}>
                  {s.name}
                  <span
                    className={classes.configChipRemove}
                    role="button"
                    aria-label={`Tirar ${s.name}`}
                    onClick={() => toggleStatus(s)}
                  >
                    <X size={10} />
                  </span>
                </span>
              ))}
            </div>
          )}
          <div className={classes.configDropdown} style={{ marginTop: 6 }}>
            {known.length === 0 && (
              <div className={classes.configEmpty}>
                Nenhum status recebido ainda. Eles aparecem aqui conforme o Care manda os avisos; até lá, digite o nome
                abaixo.
              </div>
            )}
            {known.map((s) => {
              const isSelected = statuses.some((chosen) => sameStatus(chosen, s));
              return (
                <div
                  key={(s.code || "") + ":" + s.name}
                  className={classes.configDropdownItem}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggleStatus(s)}
                >
                  <div className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}>
                    {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                  </div>
                  <span style={{ flex: 1, minWidth: 0 }}>{s.name}</span>
                  {s.code && <span style={{ fontSize: 10, color: W(0.45) }}>código {s.code}</span>}
                </div>
              );
            })}
          </div>
          <div className={classes.configInputRow} style={{ marginTop: 8 }}>
            <input
              className={classes.configInput}
              value={typed}
              maxLength={120}
              placeholder="Status que ainda não chegou"
              aria-label="Nome do status"
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTyped();
                }
              }}
            />
            <button type="button" className={classes.configAddBtn} onClick={addTyped} aria-label="Acrescentar o status">
              <Plus size={14} />
            </button>
          </div>
        </div>
      )}

      <div className={classes.configField}>
        <Label
          classes={classes}
          text="Conexão API oficial"
          help="A mensagem é um template aprovado pela Meta, enviado por esta conexão. O template se escolhe no 'Enviar Mensagem'."
        />
        <SingleList
          classes={classes}
          items={officialConnections}
          selectedId={nodeData.whatsappId}
          empty="Nenhuma conexão API oficial encontrada"
          onSelect={(id) => updateNodeData("whatsappId", id)}
        />
      </div>

      <div className={classes.configField}>
        <Label
          classes={classes}
          text="Conversa depois do envio"
          help="Se o cliente já tiver uma conversa aberta nesta conexão, a mensagem entra nela e nada muda."
        />
        <div className={classes.configOptionGroup} style={{ marginTop: 6 }} role="radiogroup" aria-label="Conversa depois do envio">
          {MODES.map((m) => (
            <div
              key={m.value}
              role="radio"
              aria-checked={mode === m.value}
              tabIndex={0}
              className={classes.configOptionBtn + (mode === m.value ? " " + classes.configOptionBtnActive : "")}
              onClick={() => setConversation({ mode: m.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setConversation({ mode: m.value });
                }
              }}
            >
              {m.label}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.5, color: W(0.6), marginTop: 6 }}>
          {MODES.find((m) => m.value === mode).desc}
        </div>
      </div>

      {mode === "open" && (
        <div className={classes.configField}>
          <Label classes={classes} text="Usuário" help="Quem fica com a conversa. A carteira do contato, se houver, vale mais." />
          <SingleList
            classes={classes}
            items={people}
            selectedId={conversation.userId}
            empty="Nenhum usuário encontrado"
            onSelect={(id) => setConversation({ userId: id })}
          />
        </div>
      )}

      {mode !== "closed" && (
        <div className={classes.configField}>
          <Label
            classes={classes}
            text={mode === "pending" ? "Fila" : "Fila (opcional)"}
            help="A fila em que a conversa entra."
          />
          <SingleList
            classes={classes}
            items={queues || []}
            selectedId={conversation.queueId}
            empty="Nenhuma fila encontrada"
            onSelect={(id) => setConversation({ queueId: id })}
            renderLabel={(q) => (
              <React.Fragment>
                <span className={classes.configDropdownColor} style={{ background: q.color || "#5B7CFF" }} />
                {q.name}
              </React.Fragment>
            )}
          />
        </div>
      )}

      <div className={classes.configField}>
        <div style={{ fontSize: 11, lineHeight: 1.55, color: W(0.6) }}>
          Ligue este node a um "Enviar Mensagem" e escolha o template. Os resultados de cada envio aparecem na página de
          resultados do fluxo.
        </div>
      </div>
    </React.Fragment>
  );
};

export default OrderStatusPanel;
